import React, { FC, useCallback, useEffect, useState } from 'react';
import { Modal } from '@design-system/modal';
import { Section } from '@design-system/section';
import { isBrowser } from '@phx-husky/browser-helpers';
import { getLocalStorageData, setLocalStorageData } from '@phx-husky/local-storage';
import { jebbitCampaignSchema } from '@root/components/JebbitCampaign/JebbitCampaign.schema';
import JebbitIframe from '@root/components/JebbitCampaign/JebbitIframe';
import useJoiValidation from '@root/hooks/useJoiValidation';
import cx from 'classnames';

import { IJebbitCampaignProps } from './models';

import './JebbitCampaign.scss';

const JebbitCampaign: FC<IJebbitCampaignProps> = ({
  title,
  src,
  closeBtn,
  isInModal,
  isModalShowedOnlyOnce,
  onCloseLocalStorageValue,
  modalAppearingDelay,
}) => {
  const { data }: { data: IJebbitCampaignProps } = useJoiValidation(
    jebbitCampaignSchema,
    'JebbitCampaign',
    {
      title,
      src,
      closeBtn,
      isInModal,
      isModalShowedOnlyOnce,
      onCloseLocalStorageValue,
      modalAppearingDelay,
    }
  );

  const shouldShowInitially = data.isModalShowedOnlyOnce
    ? !getLocalStorageData(data?.onCloseLocalStorageValue)
    : true;

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const handleModalClose = useCallback(() => {
    document.body.style.overflow = 'auto';

    setIsModalVisible(false);

    if (data.isModalShowedOnlyOnce) {
      setLocalStorageData(data?.onCloseLocalStorageValue, true);
    }
  }, [data.isModalShowedOnlyOnce, data.onCloseLocalStorageValue]);

  useEffect(() => {
    if (!shouldShowInitially) return undefined;

    const delay = (Number(data.modalAppearingDelay) || 0) * 1000;

    const timeout = setTimeout(() => {
      setIsModalVisible(true);
    }, delay);

    return () => clearTimeout(timeout);
  }, [shouldShowInitially, data.modalAppearingDelay]);

  if (data.isInModal) {
    return (
      <Modal
        isShowing={isBrowser() && isModalVisible}
        close={handleModalClose}
        closeModal={handleModalClose}
        closeAriaLabel={data.closeBtn}
        closeIconSize="large"
        showHeader
        showCloseButton
        className={cx('jebbit-campaign__modal', {
          'jebbit-campaign__modal--hidden': !isModalVisible,
        })}
      >
        <JebbitIframe src={data.src} />
      </Modal>
    );
  }

  return (
    <Section className="jebbit-campaign" element="div" mode="default">
      <JebbitIframe src={data.src} />
    </Section>
  );
};

export default JebbitCampaign;
